/**
 * What the app says about itself the first time it is opened — and every time
 * after that, until somebody says stop.
 *
 * The copy obeys the same voice rules as `sass/catalog/index.ts`: declarative,
 * deadpan, morbid where morbid is funnier, no exclamation marks, no emoji, at
 * most one lowercase English sprinkle in a line, decimal comma, U+2212 for
 * minus. The register is the catalogue's, not a manual's — a tour that reads
 * like documentation with a joke bolted on is a tour written in a second voice,
 * and an app that speaks in two voices sounds like two products.
 *
 * The difference from the catalogue is that a line here still has to teach
 * something. Every body says one true, checkable thing about the screen it is
 * standing on; the joke is how it is said, never instead of it.
 *
 * Every step either points at one element that is on screen the moment its tab
 * is shown, or at nothing at all. Nothing here may point into a sheet: the
 * spotlight measures live DOM, and an element that has to be opened first is an
 * element that is not there when it is measured.
 *
 * `edge` is the line that names what the paid trackers do not do. Claims about
 * a competitor are made only where they are defensible — MyFitnessPal's barcode
 * scanner is free and always has been, whatever the internet says — so most of
 * these are about this app rather than about that one.
 */

import type { TabKey } from '../shell/TabBar.js'

export interface TourStep {
  /** Stable key. The progress dots and the tests are keyed by it. */
  id: string
  /** The tab that has to be on screen before `anchor` can be measured. */
  tab: TabKey | null
  /** `[data-tour]` value to cut out of the scrim. Null draws no hole. */
  anchor: string | null
  /** A round hole, for the one element that is round. */
  shape?: 'circle'
  title: string
  body: string
  edge?: string
  /**
   * Shown only on an account that is not the author's, exactly like the cameo
   * pools in `sass/catalog/cameo.ts` — he does not need his own app to tell him
   * who wrote it, and the line only means anything to the person it was written
   * for.
   */
  cameo?: string
}

/** Titles are a heading, not a sentence; bodies are two clauses at most. */
export const TITLE_MAX = 32
export const BODY_MAX = 170
export const EDGE_MAX = 110

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'welcome',
    tab: null,
    anchor: null,
    title: 'Ekskursija su Glitime',
    body: 'Dvylika sustojimų. Aš — Glitimė, šio dienoraščio dietologė. Užtruks minutę.',
    cameo: 'Šitą tau padarė Saulius. Jis rašė kodą, Glitimė — nuomonę. Abu apie tave.',
  },
  {
    id: 'ring',
    tab: 'today',
    anchor: 'ring',
    shape: 'circle',
    title: 'Dienos žiedas',
    body:
      'Viduryje — kiek dar liko, o ne kiek suvalgyta. Treniruotė ir laikrodis pridedami prie ' +
      'limito. Ne, tai ne leidimas. Tai apskaita.',
    edge: 'Laikrodis ir treniruotės nesumuojami: imamas patikimiausias šaltinis, ne palankiausias.',
  },
  {
    id: 'macros',
    tab: 'today',
    anchor: 'macros',
    title: 'Trys juostos, keturi tylieji',
    body:
      'Baltymai, angliavandeniai, riebalai — stambiai. Po jais cukrus, sočiosios, druska ir ' +
      'skaidulos: keturi liudytojai, kurių niekas niekada nekviečia.',
    edge: 'MyFitnessPal nemokamai duoda tik procentus. Čia gramai, kaip ir ant pakuotės.',
  },
  {
    id: 'water',
    tab: 'today',
    anchor: 'water',
    title: 'Vanduo ir kofeinas',
    body:
      'Stiklinė — vienu paspaudimu. Įrašius kavą, atsiranda eilutė, kiek kofeino dar plaukios ' +
      'tavo kraujyje, kai gulsi miegoti.',
    edge: 'Ne miligramai per dieną, o kiek jų liks vienuoliktą vakaro. Skirtumas — tavo miegas.',
  },
  {
    id: 'meals',
    tab: 'today',
    anchor: 'meals',
    title: 'Keturi valgymai',
    body:
      'Kiekvienas su savo suma. Braukimas kairėn ištrina, „Atšaukti“ grąžina. Tuščiam valgymui ' +
      'siūloma „Kaip vakar“ — nes vakar valgei tą patį. Nesigink.',
    edge: 'Vakarykščiai pusryčiai atkeliami vienu paspaudimu. Rutina turi ir privalumų.',
  },
  {
    id: 'scan',
    tab: null,
    anchor: 'scan-tab',
    title: 'Skenuoti',
    body:
      'Vienas vaizdo ieškiklis: kodas, maistinės vertės lentelė, balsas ir lėkštės nuotrauka. ' +
      'Nežinomo kodo neatmeta — liepia fotografuoti lentelę.',
    edge: 'Kai kodo nežino niekas, kiti sako „nerasta“. Šitas paprašo nuotraukos ir išmoksta.',
  },
  {
    id: 'foods-search',
    tab: 'foods',
    anchor: 'foods-search',
    title: 'Lietuviškas katalogas',
    body:
      'Devyni tūkstančiai lietuviškų produktų, surinktų naktimis, kol miegojai. Paieška randa ' +
      'ir be lietuviškų raidžių, o fotoaparatas atpažįsta visą lėkštę.',
    edge: 'Varškė čia yra varškė, o ne trisdešimt amerikietiškų spėjimų, kaip ji galėtų atrodyti.',
  },
  {
    id: 'foods-sections',
    tab: 'foods',
    anchor: 'foods-sections',
    title: 'Trys keliai į dienyną',
    body:
      'Produktai iš parduotuvės, greitas maistas iš prekystalio, receptai iš tavo puodo. ' +
      'Receptai sveriami žali, dalijami išvirti — vanduo nėra kalorijos.',
    edge: 'Užsakymas surenkamas iš dalių: bandelė, dešrelė, padažai. Ne vienas apvalus vidurkis.',
  },
  {
    id: 'trends',
    tab: 'trends',
    anchor: 'trends-weight',
    title: 'Tendencijos',
    body:
      'Svoris glotninamas, nes vanduo nėra pažanga. Kaupiantis dienoms atsiranda savaitės ' +
      'biudžetas ir tavo paties apykaita — išmatuota, o ne paimta iš formulės.',
    edge: 'Sunkesnė vakarienė skolinama iš savaitės. Diena — apskaitos vienetas, ne nuosprendis.',
  },
  {
    id: 'goal',
    tab: 'settings',
    anchor: 'settings-goal',
    title: 'Etapas ir tikslas',
    body:
      'Metimas, palaikymas ar auginimas. Tempą pasiūlo pati ir perspėja, kai tikslas nesutampa ' +
      'su tikrove. Tikslai turi datas, tad praeitis lieka teisinga.',
    edge: 'Praėjusi savaitė vertinama pagal tuometinę normą. Istorijos čia niekas neperrašo.',
  },
  {
    id: 'tone',
    tab: 'settings',
    anchor: 'settings-tone',
    title: 'Nuotaika',
    body:
      'Aš esu Glitimė. Aštri — turiu nuomonę apie tavo dieną. Dalykiška — ta pati aš, tik ' +
      'užčiaupta. Perjungiama bet kada, be pasekmių ir be pokalbio apie jausmus.',
    edge: 'Kalorijų skaitiklis su charakteriu. Iconic.',
  },
  {
    id: 'ready',
    tab: null,
    anchor: null,
    title: 'Tiek to gero',
    body:
      'Viskas veikia be ryšio: dienynas telefone, katalogas serveryje. Įsidėk į pradžios ekraną ' +
      'ir pamiršk naršyklę. Glitimė nemiega — tai ne pasirinkimas, tai architektūra.',
  },
]
